/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, Share2, Shield, Check } from 'lucide-react';

import Footer from '../components/Footer';
import Logo from '@snipit/ui/components/Logo';

export default function LandingView() {
  const t = (str) => str;
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200 font-sans flex flex-col">
      {/* Navbar */}
      <nav className="border-b border-gray-800 bg-[#121212]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Logo vault="dev" className="w-8 h-8" />
            <span className="text-xl font-bold bg-clip-text text-transparent bg-linear-to-r from-blue-400 to-indigo-500">
              {t('SnipIT')}
            </span>
          </div>
          <div className="flex space-x-4">
            <Link 
              to="/login"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {t('Sign In')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="grow flex flex-col items-center justify-center text-center px-4 pt-20 pb-10">
        <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight">
          {t('Your Code, ')}<span className="bg-clip-text text-transparent bg-linear-to-r from-blue-400 to-indigo-500">{t('Everywhere.')}</span>
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
          {t('The ultimate snippet manager and developer tool suite. Securely store your code, API keys, and AI prompts with Zero-Egress local storage and optional Cloud Sync.')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <Link 
            to="/billing"
            className="bg-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg hover:bg-blue-700 transition-colors shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)]"
          >
            {t('Get Started for Free')}
          </Link>
        </div>

        {/* Interactive Demo Embed */}
        <div className="w-full max-w-5xl mx-auto rounded-xl overflow-hidden border border-gray-800 shadow-2xl @media (prefers-reduced-motion: reduce) { shadow-none border-none }">
          <iframe 
            src="/product-hunt-demo.html" 
            title="SnipIT Interactive Demo" 
            className="w-full aspect-video md:aspect-21/9 border-none bg-[#121212]"
            loading="lazy"
          ></iframe>
        </div>

        {/* Feature Grid */}
        <div className="text-center mt-24 mb-12 px-4">
          <h2 className="text-4xl font-extrabold text-white">{t('Everything you need, built right in.')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="bg-[#121212] p-6 rounded-2xl border border-gray-800 text-left">
            <Shield className="w-10 h-10 text-blue-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{t('Privacy First')}</h3>
            <p className="text-gray-400 text-sm">{t('Everything stays on your local machine until you explicitly turn on Cloud Sync. Your API keys remain yours.')}</p>
          </div>
          <div className="bg-[#121212] p-6 rounded-2xl border border-gray-800 text-left">
            <Terminal className="w-10 h-10 text-indigo-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{t('Developer Tools')}</h3>
            <p className="text-gray-400 text-sm">{t('Built-in JSON formatters, Base64 decoders, Regex testers, and Markdown pads right inside your workflow.')}</p>
          </div>
          <div className="bg-[#121212] p-6 rounded-2xl border border-gray-800 text-left">
            <Share2 className="w-10 h-10 text-purple-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{t('Cross-Platform Sync')}</h3>
            <p className="text-gray-400 text-sm">{t('Access your snippets across our Web Dashboard, Desktop App, and Chrome Extension instantly.')}</p>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="max-w-5xl mx-auto mt-32 mb-12 px-4 w-full">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-white mb-4">{t('Transparent Pricing')}</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">{t('Start for free. Upgrade when you need more power for team collaboration and cross-device syncing.')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free */}
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 flex flex-col relative overflow-hidden text-left">
              <h3 className="text-xl font-bold text-white mb-2 mt-2">{t('Free')}</h3>
              <div className="text-3xl font-bold text-gray-200 mb-4">$0</div>
              <p className="text-gray-400 text-sm mb-6 flex-1">{t('Perfect for individual developers getting started.')}</p>
              <ul className="space-y-3 mb-8">
                {['Local Storage Only', 'Basic Snippet Vaults', 'Standard IDE Themes'].map(f => (
                  <li key={f} className="flex items-center text-sm text-gray-300"><Check className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{t(f)}</li>
                ))}
              </ul>
              <Link to="/billing" className="w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white shadow-md shadow-gray-900/20">{t('Get Started')}</Link>
            </div>
            {/* Premium */}
            <div className="bg-[#1a1a1a] border border-blue-500 shadow-lg shadow-blue-500/10 rounded-xl p-6 flex flex-col relative overflow-hidden text-left">
              <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">{t('POPULAR')}</div>
              <h3 className="text-xl font-bold text-white mb-2 mt-2">{t('Pro')}</h3>
              <div className="text-3xl font-bold text-gray-200 mb-4">$10<span className="text-lg text-gray-500 font-normal">/mo</span></div>
              <p className="text-gray-400 text-sm mb-6 flex-1">{t('For professionals who need cross-device syncing.')}</p>
              <ul className="space-y-3 mb-8">
                {['Cloud Sync (Firebase)', 'AI Prompts Vault', 'Developer Board Access'].map(f => (
                  <li key={f} className="flex items-center text-sm text-gray-300"><Check className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{t(f)}</li>
                ))}
              </ul>
              <Link to="/billing" className="w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-900/20">{t('Get Started')}</Link>
            </div>
            {/* Enterprise */}
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 flex flex-col relative overflow-hidden text-left">
              <h3 className="text-xl font-bold text-white mb-2 mt-2">{t('Enterprise (Single)')}</h3>
              <div className="text-3xl font-bold text-gray-200 mb-4">$30<span className="text-lg text-gray-500 font-normal">/mo</span></div>
              <p className="text-gray-400 text-sm mb-6 flex-1">{t('Advanced AI & security for independent power users.')}</p>
              <ul className="space-y-3 mb-8">
                {['Advanced AI Prompts', 'Priority Support', 'Cloud Sync'].map(f => (
                  <li key={f} className="flex items-center text-sm text-gray-300"><Check className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{t(f)}</li>
                ))}
              </ul>
              <Link to="/billing" className="w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white shadow-md shadow-gray-900/20">{t('Get Started')}</Link>
            </div>
            {/* Enterprise Teams */}
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 flex flex-col relative overflow-hidden text-left">
              <h3 className="text-xl font-bold text-white mb-2 mt-2">{t('Enterprise Teams')}</h3>
              <div className="text-3xl font-bold text-gray-200 mb-4">$50<span className="text-lg text-gray-500 font-normal">/mo</span></div>
              <p className="text-gray-400 text-sm mb-6 flex-1">{t('For organizations sharing knowledge securely.')}</p>
              <ul className="space-y-3 mb-8">
                {['Organization Workspaces', 'Team Sharing (RBAC)', 'SSO Authentication', 'Dedicated Success Manager'].map(f => (
                  <li key={f} className="flex items-center text-sm text-gray-300"><Check className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{t(f)}</li>
                ))}
              </ul>
              <Link to="/billing" className="w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white shadow-md shadow-gray-900/20">{t('Get Started')}</Link>
            </div>
          </div>
        </div>

        {/* DevTime Promo Banner */}
        <div className="max-w-5xl mx-auto mt-24 mb-12 w-full px-4">
          <div className="bg-linear-to-r from-[#16161e] to-[#101016] p-8 md:p-12 rounded-3xl border border-gray-800/60 flex flex-col md:flex-row items-center gap-10 shadow-2xl relative overflow-hidden">
            {/* Subtle glow effect in the background */}
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="w-full md:w-2/5 flex justify-center relative z-10">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-gray-700/50 w-full max-w-[320px]">
                 <img src="/assets/devtime-promo.png" alt="DevTime Promo" className="w-full h-auto object-cover" />
              </div>
            </div>
            <div className="w-full md:w-3/5 text-left relative z-10">
              <p className="text-purple-400 text-xs font-bold tracking-[0.2em] uppercase mb-3">{t('MORE FROM DEVIT')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('Check out DevTime')}</h2>
              <p className="text-gray-400 mb-8 max-w-lg leading-relaxed text-base">
                {t('Take your productivity even further. Automatically track your time straight from your browser history and sync your calendar events.')}
              </p>
              <a 
                href="https://chromewebstore.google.com/detail/devtime/laacdocceefbggihaekapebfjicobpbk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#232332] hover:bg-[#2d2d3f] text-gray-200 border border-gray-700/80 px-5 py-2.5 rounded-xl font-medium transition-colors shadow-lg"
              >
                {t('View on Chrome Web Store')}
                <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
